import './AnimatedNumber.css';
import { useSignal } from '@preact/signals';
import { useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import { useReducedMotion } from '../a11y/useReducedMotion';
import { t } from '../copy/strings';
import { easeLive, easeOutCubic, isTabHidden, liveTweenMs, SNAP_MS, type TweenHandle, tween } from './tween';

/** Intro (A02/A04): de 90% ao valor em 700 ms, com 450 ms de atraso, ease-out cúbico. */
export const INTRO_FROM = 0.9;
export const INTRO_MS = 700;
export const INTRO_DELAY_MS = 450;

export interface AnimatedNumberProps {
  /** null = esqueleto ("–" em --color-fg-4). */
  value: number | null;
  /** Formatador de §13.4 (fmtInt, fmtPct, fmtSections…). */
  format: (v: number) => string;
  /** Texto da cópia .sr-only quando difere do visível (abreviações por extenso, sinais; §15.4.3). Padrão: format. */
  srFormat?: (v: number) => string;
  /** Primeira montagem dentro da janela da intro (A02). */
  intro?: boolean;
  /** Classes extras do dono (tipografia). */
  class?: string;
}

const finite = (v: number | null): v is number => v !== null && Number.isFinite(v);

/**
 * Número que corre (§3.8): do valor exibido ao novo em --dur-live-tween com a curva de A04, num laço rAF compartilhado
 * (tween.ts). Salta direto com duração ≤ 300 ms (replay, salto > 10 min), movimento reduzido ou aba oculta. A cópia
 * .sr-only leva o valor final desde o primeiro quadro (AC-3.8-28, §15.4.3); a parte animada é aria-hidden.
 */
export function AnimatedNumber({ value, format, srFormat, intro = false, class: extra }: AnimatedNumberProps) {
  const reduced = useReducedMotion();
  const formatRef = useRef(format);
  formatRef.current = format;
  /** Valor numérico na tela agora (null = nada exibido ainda ou esqueleto). */
  const shown = useRef<number | null>(null);
  const job = useRef<TweenHandle | null>(null);
  const text = useSignal(value === null ? t('common.dash') : format(value));

  // Dispara só com valor novo (um por snapshot); a preferência e a intro valem no instante da troca. Mudar a
  // preferência no meio de uma interpolação é tratado por tween.ts, que conclui tudo no mesmo quadro (§15.6.1).
  useLayoutEffect(() => {
    const paint = (v: number) => {
      shown.current = v;
      text.value = formatRef.current(v);
    };
    const onDone = () => {
      job.current = null;
    };
    job.current?.cancel();
    job.current = null;
    if (!finite(value)) {
      shown.current = null;
      text.value = value === null ? t('common.dash') : formatRef.current(value);
      return;
    }
    const from = shown.current;
    const still = reduced || isTabHidden();
    if (from === null) {
      // Primeiro valor: dentro da intro corre de 90% ao valor; fora dela aparece direto.
      if (!intro || still) {
        paint(value);
        return;
      }
      const handle = tween({
        from: value * INTRO_FROM,
        to: value,
        duration: INTRO_MS,
        delay: INTRO_DELAY_MS,
        ease: easeOutCubic,
        onUpdate: paint,
        onDone,
      });
      if (handle.active) job.current = handle;
      return;
    }
    const ms = liveTweenMs();
    if (still || ms <= SNAP_MS || from === value) {
      paint(value);
      return;
    }
    const handle = tween({ from, to: value, duration: ms, ease: easeLive, onUpdate: paint, onDone });
    if (handle.active) job.current = handle;
  }, [value]);

  // Formatador trocado sem valor novo: repinta parado, sem interpolar.
  useLayoutEffect(() => {
    if (job.current === null && shown.current !== null) text.value = formatRef.current(shown.current);
  });

  useEffect(
    () => () => {
      job.current?.cancel();
      job.current = null;
    },
    [],
  );

  const empty = value === null;
  const final = empty ? t('common.dash') : (srFormat ?? format)(value);
  return (
    <span
      class={extra ? `animated-number ${extra}` : 'animated-number'}
      data-testid="animated-number"
      data-state={empty ? 'empty' : undefined}
    >
      <span class="animated-number__value" aria-hidden="true">
        {text}
      </span>
      <span class="sr-only">{final}</span>
    </span>
  );
}
